import { Button, Dialog, DialogBody, DialogFooter, InputGroup } from '@blueprintjs/core';
import React, { useState } from 'react';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import { observer } from 'mobx-react-lite';
import Notification from '../Notification';
import DataSyncCgObjectsStore from '../../../../stores/object/DataSyncCgObjectsStore';
import { ZipApi } from '../../../../lib/ZipApi';
import { directoryStore } from '../../../../stores/DirectoryStore';
import { getApiErrorMessage } from '../../../../lib/ZipApiError';

interface Props {
  initialObjectName: string;
  isOpen: boolean;
  onSave: (objectName: string, baseType?: string) => void;
  onClose: () => void;
  dataSyncCGObjectsStore: DataSyncCgObjectsStore;
}

export const CGObjectCreateDialog = observer((props: Props) => {

  const { initialObjectName, isOpen, onSave, onClose, dataSyncCGObjectsStore } = props;

  const objectNamePattern = /(^\d)|[^a-zA-Z0-9_]/;

  const [name, setName] = useState<string>(initialObjectName);
  const [baseType, setBaseType] = useState<string | undefined>(
    dataSyncCGObjectsStore?.getBaseTypeDefinitions().length > 0 ? '_Item' : undefined
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [error, setError] = React.useState<string>('');

  const graphSourceId = dataSyncCGObjectsStore?.getResolvedGraphSourceId();

  const saveObjectName = async () => {
    const existingObjects = dataSyncCGObjectsStore?.getDestinationObjectList() || [];
    if (!name || objectNamePattern.test(name)) {
      setError('Name must start with a letter or underscore and contain only letters, digits, or underscores.');
    } else if (existingObjects?.find((existingObject) =>
      existingObject.object_name.toLowerCase() === name.toLowerCase())
    ) {
      setError(`A content type with the name '${name}' already exists`);
    } else {
      const instanceId = dataSyncCGObjectsStore?.getInstanceId();
      const trackerId = directoryStore.trackerId;

      if (instanceId && trackerId) {
        try {
          setIsValidating(true);
          const result = await ZipApi.checkCGTypeExists(trackerId, instanceId, name);

          if (result.exists) {
            setError(result.message || `A content type with the name '${name}' already exists.`);
            setIsValidating(false);
            return;
          }
        } catch (err: unknown) {
          const message = await getApiErrorMessage(err, 'Unable to validate type name');
          setError(message);
          setIsValidating(false);
          return;
        }
      }

      setIsValidating(false);
      const baseTypeIndex = dataSyncCGObjectsStore?.getBaseTypeDefinitions()?.findIndex(
        (baseTypeDefinition) => baseTypeDefinition.name === baseType);
      onSave(name, (baseTypeIndex !== undefined && baseTypeIndex >= 0) ? baseType : undefined);
    }
  };

  const baseTypeNames = () => {
    return (
      dataSyncCGObjectsStore?.getBaseTypeDefinitions().map((baseTypeDefinition) => ({
        text: baseTypeDefinition.display_name || baseTypeDefinition.name,
        value: baseTypeDefinition.name
      })) || []
    );
  };

  return (
    <Dialog title="Add New Object" isOpen={isOpen} onClose={onClose}>
      <DialogBody>
          <Label label="Name">
            <InputGroup value={name} onChange={(e) => setName(e.target.value)} />
            <Notification message={error} show={!!error} type="error"/>
            <Notification
              message={`This content type will be added to Graph source '${graphSourceId}'.`}
              show={!!graphSourceId}
              type="info"
            />
          </Label>

          {dataSyncCGObjectsStore?.getBaseTypeDefinitions().length > 0 && <Label label="Base Type">
          <DropDown
            initialContent="Select Base Type"
            items={baseTypeNames()}
            loading={dataSyncCGObjectsStore?.loadingBaseTypes || false}
            onItemSelect={(item: SelectItem) => {
              setBaseType(item.value);
            }}
            value={baseType}
            width={390}
          />
        </Label>}
      </DialogBody>
      <DialogFooter actions={
        <>
          <Button text="Cancel" minimal={true} onClick={() => onClose()} disabled={isValidating}/>
          <Button
            intent="primary"
            text="Apply"
            onClick={() => saveObjectName()}
            loading={isValidating}
            disabled={isValidating}
          />
        </>
      } />
     </Dialog>
  );
});
